import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { productService } from '../services/productService';
import { userService } from '../services/userService';
import { useAuth } from '../services/AuthContext';
import { productUrl } from '../utils/seo';
import api from '../services/api';
import BannerCarousel from '../components/BannerCarousel';
import UserLink from '../components/UserLink';
import FilterModal from '../components/FilterModal';
import CategoryFilterSelect from '../components/CategoryFilterSelect';
import InlineAgent from '../components/InlineAgent';

const SESSION_KEY = 'ml_pl_state';
const HISTORY_KEY = 'ml_search_history';
const MAX_HISTORY = 20;

const DEFAULT_FILTERS = {
  minPrice: '', maxPrice: '',
  hasInterested: 0,
  priceType: null,
  condition: null,
  madeToOrder: null,
};

function countActiveFilters(f) {
  let c = 0;
  if (f.minPrice) c++;
  if (f.maxPrice) c++;
  if (f.hasInterested !== 0) c++;
  if (f.priceType) c++;
  if (f.condition) c++;
  if (f.madeToOrder !== null && f.madeToOrder !== undefined) c++;
  return c;
}

function generateSeed() {
  return Math.random().toString(36).substring(2, 12) + Date.now().toString(36);
}

function loadSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      sessionStorage.removeItem(SESSION_KEY);
      return JSON.parse(raw);
    }
  } catch {}
  return null;
}

// ── URL ↔ state sync ──────────────────────────────────────────────────────────
function parseUrlParams() {
  const p = new URLSearchParams(window.location.search);
  // Return null if there are no meaningful params (user just landed on "/")
  if (!p.has('seed') && !p.has('q') && !p.has('categoria') && !p.has('vista')) return null;
  return {
    q: p.get('q') || '',
    categoria: p.get('categoria') || '',
    vista: p.get('vista') || 'grid',
    seed: p.get('seed') || generateSeed(),
    minPrice: p.get('minPrice') || '',
    maxPrice: p.get('maxPrice') || '',
    priceType: p.get('priceType') || null,
    condition: p.get('condition') || null,
    madeToOrder: p.has('madeToOrder') ? p.get('madeToOrder') === 'true' : null,
    hasInterested: p.has('hasInterested') ? parseInt(p.get('hasInterested'), 10) : 0,
  };
}

function buildUrlParams(search, category, viewMode, seed, filters) {
  const p = new URLSearchParams();
  p.set('seed', seed);
  if (search) p.set('q', search);
  if (category) p.set('categoria', category);
  if (viewMode !== 'grid') p.set('vista', viewMode);
  if (filters.minPrice) p.set('minPrice', filters.minPrice);
  if (filters.maxPrice) p.set('maxPrice', filters.maxPrice);
  if (filters.priceType) p.set('priceType', filters.priceType);
  if (filters.condition) p.set('condition', filters.condition);
  if (filters.madeToOrder !== null && filters.madeToOrder !== undefined) p.set('madeToOrder', String(filters.madeToOrder));
  if (filters.hasInterested !== 0) p.set('hasInterested', String(filters.hasInterested));
  return p.toString();
}

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveHistory(history) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
  } catch {}
}

// ── Reel item (photos & videos) ──────────────────────────────────────────────
function ReelItem({ product, showVideo, onNavigate, eager }) {
  const isVideo = showVideo && product.videoUrl;

  return (
    <div className={`reel-item${isVideo ? ' reel-item--video' : ''}`}>
      {isVideo ? (
        <video
          className="reel-media"
          src={product.videoUrl}
          controls
          playsInline
          preload="metadata"
        />
      ) : product.imageUrls?.length > 0 ? (
        <img
          className="reel-media"
          src={product.imageUrls[0]}
          alt={product.title}
          loading={eager ? 'eager' : 'lazy'}
        />
      ) : (
        <div className="reel-media-placeholder">
          {showVideo ? '🎬' : '📷'}
        </div>
      )}

      <div className="reel-overlay">
        <div className="reel-badges">
          {product.category && (
            <span className="badge badge-category">{product.category}</span>
          )}
          {product.condition && (
            <span className={`badge ${product.condition === 'NEW' ? 'badge-new' : 'badge-used'}`}>
              {product.condition === 'NEW' ? 'Nuevo' : 'Usado'}
            </span>
          )}
        </div>
        <h3 className="reel-title">{product.title}</h3>
        <div className="reel-price">
          {product.priceOnConsultation
            ? 'Precio a consultar'
            : `${product.currency === 'USD' ? 'US$' : '$'}${Number(product.price).toLocaleString('es-UY')}`}
        </div>
        <div className="reel-meta">
          <UserLink
            username={product.sellerUsername}
            rating={product.sellerRating}
            reviewCount={product.sellerReviewCount}
            style={{ color: 'white' }}
          />
          {product.department && ` · 📍 ${product.department}`}
          {product.interestCount > 0 && ` · ❤️ ${product.interestCount}`}
        </div>
        <button
          className="btn btn-primary reel-cta"
          onClick={() => onNavigate(product)}
        >
          Ver publicación →
        </button>
      </div>
    </div>
  );
}

// ── Search History Panel ─────────────────────────────────────────────────────
function SearchHistoryPanel({ open, onClose, history, onSelect, onClear }) {
  return (
    <>
      {open && <div className="history-overlay" onClick={onClose} />}
      <div className={`history-panel${open ? ' history-panel--open' : ''}`}>
        <div className="history-panel-header">
          <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Historial de búsquedas</span>
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            style={{ padding: '0.3rem 0.7rem', fontSize: '0.85rem', lineHeight: 1 }}
          >Cerrar</button>
        </div>
        <div className="history-panel-body">
          {history.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>
              No hay búsquedas guardadas
            </div>
          ) : (
            <>
              {history.map((item, idx) => (
                <button
                  key={item.seed + idx}
                  className="history-item"
                  onClick={() => onSelect(item)}
                >
                  <div className="history-item-label">
                    {item.search || item.category || 'Todas las publicaciones'}
                    {item.search && item.category && (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}> en {item.category}</span>
                    )}
                  </div>
                  <div className="history-item-meta">
                    {item.viewMode === 'photos' && '📷 '}
                    {item.viewMode === 'videos' && '🎬 '}
                    {item.resultCount != null && `${item.resultCount} resultados · `}
                    {new Date(item.timestamp).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </button>
              ))}
              <button
                className="btn btn-secondary btn-sm"
                style={{ marginTop: '0.75rem', width: '100%' }}
                onClick={onClear}
              >
                Limpiar historial
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function ProductList() {
  // ── Read session state synchronously on first render ──────────────────────
  const sessionRef = useRef(null);
  const skipFetchRef = useRef(false);
  const pendingReelScrollRef = useRef(null);

  // Lazy initializers: URL params > session restore > defaults
  const urlParamsRef = useRef(null);

  const [viewMode, setViewMode] = useState(() => {
    const u = parseUrlParams();
    if (u) { urlParamsRef.current = u; return u.vista; }
    const s = loadSession();
    if (s) { sessionRef.current = s; skipFetchRef.current = true; }
    return s?.viewMode ?? 'grid';
  });
  const [products, setProducts] = useState(() => sessionRef.current?.products ?? []);
  const [hasMore, setHasMore] = useState(() => sessionRef.current?.hasMore ?? false);
  const [searchInput, setSearchInput] = useState(() => urlParamsRef.current?.q ?? sessionRef.current?.searchInput ?? '');
  const [categoryInput, setCategoryInput] = useState(() => urlParamsRef.current?.categoria ?? sessionRef.current?.categoryInput ?? '');
  const [activeSearch, setActiveSearch] = useState(() => urlParamsRef.current?.q ?? sessionRef.current?.activeSearch ?? '');
  const [activeCategory, setActiveCategory] = useState(() => urlParamsRef.current?.categoria ?? sessionRef.current?.activeCategory ?? '');
  const [filters, setFilters] = useState(() => {
    const u = urlParamsRef.current;
    if (u) return {
      minPrice: u.minPrice, maxPrice: u.maxPrice,
      hasInterested: u.hasInterested, priceType: u.priceType,
      condition: u.condition, madeToOrder: u.madeToOrder,
    };
    return sessionRef.current?.filters ?? { ...DEFAULT_FILTERS };
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [loading, setLoading] = useState(false);

  // Random seed for reproducible ordering — URL seed > session seed > new
  const seedRef = useRef(urlParamsRef.current?.seed ?? sessionRef.current?.seed ?? generateSeed());

  // Search history
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(loadHistory);

  // Banner state
  const [bannerConfig, setBannerConfig] = useState({ banners: [], autoplay: true, intervalSeconds: 5 });

  // Inline agent (desktop only)
  const [inlineAgentProductId, setInlineAgentProductId] = useState(null);

  // Top categories
  const { isAuthenticated, user } = useAuth();
  const isAdmin = isAuthenticated && user?.roles?.includes('ADMIN');
  const [topCategories, setTopCategories] = useState([]);

  const pageRef = useRef(sessionRef.current?.page ?? 0);
  const fetchingRef = useRef(false);
  const gridSentinelRef = useRef(null);
  const reelContainerRef = useRef(null);
  const [reelIndex, setReelIndex] = useState(0);
  const preloadedRef = useRef(new Set());
  const totalResultsRef = useRef(0);
  const navigate = useNavigate();

  const pageSize = viewMode === 'grid' ? 12 : 6;

  // ── Load banner config once ────────────────────────────────────────────────
  useEffect(() => {
    api.get('/banners').then((res) => setBannerConfig(res.data)).catch(() => {});
  }, []);

  // ── Load top categories for authenticated users ──────────────────────────
  useEffect(() => {
    if (isAuthenticated) {
      userService.getTopCategories()
        .then((res) => setTopCategories(res.data || []))
        .catch(() => {});
    }
  }, [isAuthenticated]);

  // ── Fetch helper ──────────────────────────────────────────────────────────
  const fetchPage = useCallback(async (pageNum, search, category, replace, extraParams, seed, flt) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const params = { page: pageNum, size: pageSize, seed: seed || seedRef.current };
      if (search) params.search = search;
      if (category) params.category = category;
      if (extraParams) Object.assign(params, extraParams);
      // Advanced filters
      const f = flt || filters;
      if (f.minPrice) params.minPrice = f.minPrice;
      if (f.maxPrice) params.maxPrice = f.maxPrice;
      if (f.priceType) params.priceType = f.priceType;
      if (f.condition) params.condition = f.condition;
      if (f.madeToOrder !== null && f.madeToOrder !== undefined) params.madeToOrder = f.madeToOrder;
      if (f.hasInterested !== 0) params.hasInterested = f.hasInterested;
      const res = await productService.getAll(params);
      const content = res.data.content || [];
      setProducts((prev) => replace ? content : [...prev, ...content]);
      pageRef.current = pageNum;
      setHasMore(!res.data.last);
      if (replace) totalResultsRef.current = res.data.totalElements || content.length;
    } catch {
      // silently fail
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, [pageSize, filters]);

  // ── Save to history after initial fetch completes ─────────────────────────
  const saveToHistory = useCallback((search, category, seed, vm, flt) => {
    // Slight delay to let totalResultsRef update
    setTimeout(() => {
      const entry = {
        search: search || '',
        category: category || '',
        seed,
        viewMode: vm,
        timestamp: Date.now(),
        resultCount: totalResultsRef.current,
        filters: flt || { ...DEFAULT_FILTERS },
      };
      setHistory((prev) => {
        const filtered = prev.filter(
          (h) => h.seed !== seed
        );
        const updated = [entry, ...filtered].slice(0, MAX_HISTORY);
        saveHistory(updated);
        return updated;
      });
    }, 300);
  }, []);

  // ── Reset & fetch, or restore from session ────────────────────────────────
  useEffect(() => {
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      // Restore scroll position
      const s = sessionRef.current;
      sessionRef.current = null;
      if (s) {
        if (s.viewMode === 'grid') {
          requestAnimationFrame(() => window.scrollTo(0, s.scrollY ?? 0));
        } else {
          pendingReelScrollRef.current = s.reelScrollTop ?? 0;
        }
      }
      // Sync URL with restored session state
      const qs = buildUrlParams(activeSearch, activeCategory, viewMode, seedRef.current, filters);
      window.history.replaceState(null, '', `/?${qs}`);
      return;
    }
    setProducts([]);
    pageRef.current = 0;
    setHasMore(false);
    const extra = viewMode === 'photos' ? { hasImages: true } : viewMode === 'videos' ? { hasVideo: true } : {};
    fetchPage(0, activeSearch, activeCategory, true, extra, seedRef.current, filters);
    saveToHistory(activeSearch, activeCategory, seedRef.current, viewMode, filters);
    // Sync state → URL
    const qs = buildUrlParams(activeSearch, activeCategory, viewMode, seedRef.current, filters);
    window.history.replaceState(null, '', `/?${qs}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSearch, activeCategory, viewMode, filters, searchTrigger, fetchPage]);

  // ── Reel scroll restoration after products render ─────────────────────────
  useEffect(() => {
    if (pendingReelScrollRef.current !== null && reelContainerRef.current && products.length > 0) {
      reelContainerRef.current.scrollTop = pendingReelScrollRef.current;
      pendingReelScrollRef.current = null;
    }
  }, [products]);

  // ── Reel: track visible index + preload upcoming images ───────────────────
  useEffect(() => {
    if (viewMode === 'grid') return;
    const container = reelContainerRef.current;
    if (!container) return;

    const updateIndex = () => {
      const itemH = container.clientHeight;
      if (itemH === 0) return;
      const idx = Math.round(container.scrollTop / itemH);
      setReelIndex(idx);
    };

    container.addEventListener('scroll', updateIndex, { passive: true });
    updateIndex();
    return () => container.removeEventListener('scroll', updateIndex);
  }, [viewMode, products.length]);

  useEffect(() => {
    if (viewMode === 'grid' || products.length === 0) return;
    const PRELOAD_AHEAD = 3;
    const showVideo = viewMode === 'videos';

    for (let i = reelIndex; i <= reelIndex + PRELOAD_AHEAD && i < products.length; i++) {
      const p = products[i];
      if (!p) continue;

      if (showVideo) continue; // videos use preload="metadata" already

      const url = p.imageUrls?.[0];
      if (url && !preloadedRef.current.has(url)) {
        preloadedRef.current.add(url);
        const img = new Image();
        img.src = url;
      }
    }
  }, [viewMode, reelIndex, products]);

  // ── Grid infinite scroll ───────────────────────────────────────────────────
  useEffect(() => {
    if (viewMode !== 'grid') return;
    const sentinel = gridSentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !fetchingRef.current) {
        const extra = {};
        fetchPage(pageRef.current + 1, activeSearch, activeCategory, false, extra, seedRef.current, filters);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [viewMode, hasMore, activeSearch, activeCategory, filters, fetchPage]);

  // ── Reel infinite scroll ───────────────────────────────────────────────────
  useEffect(() => {
    if (viewMode === 'grid') return;
    const container = reelContainerRef.current;
    if (!container) return;
    const extra = viewMode === 'photos' ? { hasImages: true } : { hasVideo: true };
    const handleScroll = () => {
      if (!hasMore || fetchingRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - clientHeight) {
        fetchPage(pageRef.current + 1, activeSearch, activeCategory, false, extra, seedRef.current, filters);
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [viewMode, hasMore, activeSearch, activeCategory, filters, fetchPage]);

  // ── Navigation helpers ────────────────────────────────────────────────────
  const goToProduct = useCallback((product) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({
      viewMode,
      scrollY: window.scrollY,
      reelScrollTop: reelContainerRef.current?.scrollTop ?? 0,
      activeSearch,
      activeCategory,
      searchInput,
      categoryInput,
      products,
      hasMore,
      page: pageRef.current,
      seed: seedRef.current,
      filters,
    }));
    navigate(productUrl(product));
  }, [viewMode, activeSearch, activeCategory, searchInput, categoryInput, products, hasMore, filters, navigate]);

  const handleSearch = (e) => {
    e.preventDefault();
    seedRef.current = generateSeed();
    // Auto-detect condition keywords in search text
    const lower = searchInput.toLowerCase().trim();
    if (/\busados?\b/.test(lower)) {
      setFilters((prev) => ({ ...prev, condition: 'USED' }));
    } else if (/\bnuevos?\b/.test(lower)) {
      setFilters((prev) => ({ ...prev, condition: 'NEW' }));
    }
    setActiveSearch(searchInput);
    setActiveCategory(categoryInput);
    setSearchTrigger((t) => t + 1);
  };

  const clearFilters = () => {
    seedRef.current = generateSeed();
    setSearchInput(''); setCategoryInput('');
    setActiveSearch(''); setActiveCategory('');
    setFilters({ ...DEFAULT_FILTERS });
    // URL will be updated by the main fetch effect
  };

  const handleHistorySelect = (item) => {
    setHistoryOpen(false);
    // Restore the exact same seed to reproduce the same order
    seedRef.current = item.seed;
    setSearchInput(item.search || '');
    setCategoryInput(item.category || '');
    if (item.filters) setFilters(item.filters);
    if (item.viewMode && item.viewMode !== viewMode) {
      setViewMode(item.viewMode);
    }
    setActiveSearch(item.search || '');
    setActiveCategory(item.category || '');
  };

  const handleClearHistory = () => {
    setHistory([]);
    localStorage.removeItem(HISTORY_KEY);
  };

  const activeFilterCount = countActiveFilters(filters);
  const hasActiveFilters = activeSearch || activeCategory || activeFilterCount > 0;

  const metaTitle = activeSearch
    ? `Buscar "${activeSearch}"${activeCategory ? ` en ${activeCategory}` : ''} — MercadoLibertario`
    : activeCategory
      ? `${activeCategory} — MercadoLibertario`
      : 'MercadoLibertario — Compra y vende libremente en Uruguay';
  const metaDescription = activeSearch || activeCategory
    ? `Resultados de ${activeSearch || activeCategory} en MercadoLibertario. Marketplace libre de Uruguay.`
    : 'MercadoLibertario — Marketplace libre de Uruguay. Compra y vende productos usados, artesanales y servicios profesionales. Sin comisiones, sin intermediarios.';

  return (
    <div>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={`${window.location.origin}/logo512.png`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="MercadoLibertario" />
      </Helmet>

      {/* Filter Modal */}
      {filterModalOpen && (
        <FilterModal
          filters={filters}
          onApply={(f) => { seedRef.current = generateSeed(); setFilters(f); }}
          onClose={() => setFilterModalOpen(false)}
        />
      )}

      {/* Search History Panel */}
      <SearchHistoryPanel
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={history}
        onSelect={handleHistorySelect}
        onClear={handleClearHistory}
      />

      {/* Hero */}
      <div className="hero">
        <h2>Mercado Sin Vueltas</h2>
        <p>Comprá y vendé libremente. Sin intermediarios, sin burocracia.<br />100% Uruguay.</p>
      </div>

      {/* Banner carousel — shown only in grid mode */}
      {viewMode === 'grid' && bannerConfig.banners.length > 0 && (
        <BannerCarousel
          banners={bannerConfig.banners}
          autoplay={bannerConfig.autoplay}
          intervalSeconds={bannerConfig.intervalSeconds}
        />
      )}

      {/* Search bar + view mode selector */}
      <form onSubmit={handleSearch} className="search-bar" style={{ flexWrap: 'wrap' }}>
        <input
          type="text"
          className="search-input"
          placeholder="Buscar productos..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <CategoryFilterSelect
          className="search-input"
          style={{ maxWidth: 200, borderRadius: 'var(--radius-md)' }}
          value={categoryInput}
          onChange={(e) => setCategoryInput(e.target.value)}
        />
        <button type="submit" className="search-btn">Buscar</button>
        {hasActiveFilters && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={clearFilters}>
            Limpiar
          </button>
        )}

        {/* Advanced filters */}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setFilterModalOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
        >
          Filtros
          {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
        </button>

        {/* History toggle */}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setHistoryOpen((v) => !v)}
          title="Historial de búsquedas"
          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
        >
          <span style={{ fontSize: '1rem' }}>🕘</span>
          {history.length > 0 && (
            <span style={{ fontSize: '0.72rem', background: 'var(--color-cyan)', color: 'var(--bg-primary)', borderRadius: '50%', width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
              {history.length}
            </span>
          )}
        </button>

        {/* View mode selector */}
        <div className="view-mode-selector" title="Cambiar vista">
          <button
            type="button"
            className={`view-mode-btn${viewMode === 'grid' ? ' active' : ''}`}
            onClick={() => { seedRef.current = generateSeed(); setViewMode('grid'); }}
            title="Grilla"
          >⊞</button>
          <button
            type="button"
            className={`view-mode-btn${viewMode === 'photos' ? ' active' : ''}`}
            onClick={() => { seedRef.current = generateSeed(); setViewMode('photos'); }}
            title="Modo fotos"
          >📷</button>
          <button
            type="button"
            className={`view-mode-btn${viewMode === 'videos' ? ' active' : ''}`}
            onClick={() => { seedRef.current = generateSeed(); setViewMode('videos'); }}
            title="Modo videos"
          >🎬</button>
        </div>
      </form>

      {/* ── Top category pills ── */}
      {topCategories.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', padding: '0 1rem', marginBottom: '1rem' }}>
          {topCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`btn btn-sm ${activeCategory === cat ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => {
                if (activeCategory === cat) {
                  setCategoryInput('');
                  setActiveCategory('');
                } else {
                  setCategoryInput(cat);
                  setActiveCategory(cat);
                }
                seedRef.current = generateSeed();
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* ── Grid mode ── */}
      {viewMode === 'grid' && (
        <>
          {loading && products.length === 0 ? (
            <div className="loading"><span className="spinner" /></div>
          ) : products.length === 0 ? (
            <div className="no-results">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
              <p>No hay publicaciones disponibles.</p>
              {hasActiveFilters && (
                <button className="btn btn-secondary" onClick={clearFilters} style={{ marginTop: '0.75rem' }}>
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="product-grid">
                {products.map((p) => (
                  <React.Fragment key={p.id}>
                    <div
                      className={`product-card${inlineAgentProductId === p.id ? ' product-card--agent-active' : ''}`}
                      onClick={() => goToProduct(p)}
                    >
                      {p.imageUrls?.length > 0 ? (
                        <div className="product-card-img">
                          <img src={p.imageUrls[0]} alt={p.title} loading="lazy" decoding="async" />
                        </div>
                      ) : (
                        <div className="product-card-img-placeholder">🛒</div>
                      )}
                      <div className="product-card-body">
                        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                          {p.category && <span className="badge badge-category">{p.category}</span>}
                          {p.condition && (
                            <span className={`badge ${p.condition === 'NEW' ? 'badge-new' : 'badge-used'}`}>
                              {p.condition === 'NEW' ? 'Nuevo' : 'Usado'}
                            </span>
                          )}
                        </div>
                        <h3>{p.title}</h3>
                        <div className="product-price">
                          {p.priceOnConsultation
                            ? 'Precio a consultar'
                            : `${p.currency === 'USD' ? 'US$' : '$'}${Number(p.price).toLocaleString('es-UY')}`}
                        </div>
                        <div className="product-seller" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {p.department && <span>📍 {p.department}</span>}
                          <span style={{ color: 'var(--border-default)' }}>·</span>
                          <UserLink
                            username={p.sellerUsername}
                            rating={p.sellerRating}
                            reviewCount={p.sellerReviewCount}
                          />
                          {p.interestCount > 0 && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                              · ❤️ {p.interestCount}
                            </span>
                          )}
                          {p.stock > 1 && (
                            <span className="badge badge-active" style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}>
                              {p.stock} disp.
                            </span>
                          )}
                        </div>
                        {!isAdmin && (
                          <button
                            className="btn btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.innerWidth > 768) {
                                setInlineAgentProductId(prev => prev === p.id ? null : p.id);
                              } else {
                                navigate(`/agent?productId=${p.id}`);
                              }
                            }}
                            style={{
                              marginTop: '0.5rem',
                              background: 'linear-gradient(135deg, #7c3aed22, #7c3aed11)',
                              border: '1px solid #7c3aed44',
                              color: '#a78bfa',
                              fontSize: '0.75rem',
                              padding: '0.3rem 0.6rem',
                              borderRadius: 'var(--radius-md)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.3rem',
                              width: 'fit-content',
                            }}
                            title="Preguntale al agente IA sobre este producto"
                          >
                            🤖 Preguntale al agente
                          </button>
                        )}
                      </div>
                    </div>
                    {inlineAgentProductId === p.id && (
                      <InlineAgent
                        product={p}
                        onClose={() => setInlineAgentProductId(null)}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div ref={gridSentinelRef} style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {loading && <span className="spinner" />}
              </div>
            </>
          )}
        </>
      )}

      {/* ── Reel modes (photos & videos) ── */}
      {(viewMode === 'photos' || viewMode === 'videos') && (
        <div className="reel-outer">
          {products.length === 0 && !loading ? (
            <div className="no-results" style={{ color: 'white' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                {viewMode === 'videos' ? '🎬' : '📷'}
              </div>
              <p>No hay publicaciones disponibles.</p>
              {hasActiveFilters && (
                <button className="btn btn-secondary" onClick={clearFilters} style={{ marginTop: '0.75rem' }}>
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <div className="reel-container" ref={reelContainerRef}>
              {loading && products.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <span className="spinner" style={{ width: 40, height: 40, borderWidth: 3 }} />
                </div>
              ) : (
                <>
                  {products.map((p, i) => (
                    <ReelItem
                      key={p.id}
                      product={p}
                      showVideo={viewMode === 'videos'}
                      onNavigate={goToProduct}
                      eager={i >= reelIndex && i <= reelIndex + 3}
                    />
                  ))}
                  {loading && (
                    <div className="reel-item reel-loading-item">
                      <span className="spinner" style={{ width: 40, height: 40, borderWidth: 3 }} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProductList;
